import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Plus, Copy, Check, Tag, Calendar,
  Users, AlertCircle, Infinity, ToggleLeft, ToggleRight, Pencil, Trash2, Globe,
} from "lucide-react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import CreateCouponModal from "../../components/modals/CreateCouponModal";

const getId = (obj) => obj?.id ?? obj?._id;

// ── Helpers ────────────────────────────────────────────────────────────────
function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

function fmtCOP(n) {
  return Number(n).toLocaleString("es-CO");
}

function accentConfig(coupon) {
  const expired = coupon.endDate && new Date(coupon.endDate) < new Date();
  const days    = daysUntil(coupon.endDate);
  if (!coupon.active) return { strip: "bg-gray-400",    text: "from-gray-400 to-gray-500" };
  if (expired)        return { strip: "bg-red-500",     text: "from-red-500 to-red-600"   };
  if (days !== null && days <= 7)
                      return { strip: "bg-amber-500",   text: "from-amber-400 to-amber-500"};
  if (coupon.discountType === "fixed")
                      return { strip: "bg-emerald-600", text: "from-emerald-500 to-emerald-700" };
  return              { strip: "bg-indigo-600",         text: "from-indigo-500 to-indigo-700" };
}

function statusBadge(coupon) {
  const expired = coupon.endDate && new Date(coupon.endDate) < new Date();
  const days    = daysUntil(coupon.endDate);
  if (!coupon.active)              return { label: "Inactivo",          cls: "bg-gray-100 text-gray-500"   };
  if (expired)                     return { label: "Vencido",           cls: "bg-red-100 text-red-600"     };
  if (days !== null && days <= 7)  return { label: `Vence en ${days}d`, cls: "bg-amber-100 text-amber-700" };
  return                                  { label: "Activo",            cls: "bg-emerald-100 text-emerald-700" };
}

// ── CouponCard ─────────────────────────────────────────────────────────────
function CouponCard({ coupon, ownCompanyId, onToggle, onEdit, onDelete }) {
  const [copied, setCopied] = useState(false);
  // Un cupón de plataforma (companyId null) lo gestiona el admin — el panel de
  // tienda solo lo ve de referencia, sin poder tocarlo.
  const isMine = coupon.companyId === ownCompanyId;

  const isExpired   = coupon.endDate && new Date(coupon.endDate) < new Date();
  const isUnlimited = coupon.maxUses === null;
  const days        = daysUntil(coupon.endDate);
  const usagePct    = (!isUnlimited && coupon.maxUses > 0)
    ? Math.min(100, Math.round((coupon.usedCount / coupon.maxUses) * 100))
    : 0;

  const { strip, text } = accentConfig(coupon);
  const badge           = statusBadge(coupon);
  const isActive        = coupon.active && !isExpired;

  const handleCopy = () => {
    navigator.clipboard?.writeText(coupon.code).catch(() => {});
    setCopied(true);
    toast.success(`Código ${coupon.code} copiado al portapapeles`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      className={`flex rounded-2xl overflow-visible shadow-sm border transition-all duration-200 ${
        isActive ? "border-gray-200 hover:shadow-md hover:-translate-y-0.5" : "border-gray-100 opacity-65"
      }`}
    >
      {/* ── Left accent ─────────────────────────────────── */}
      <div className={`relative bg-gradient-to-b ${text} shrink-0 w-[88px] sm:w-[100px] flex flex-col items-center justify-center py-5 px-3 rounded-l-2xl`}>
        {/* Discount value */}
        <p className="text-[28px] sm:text-[32px] font-black text-white leading-none text-center">
          {coupon.discountType === "percentage"
            ? `${coupon.discountValue}%`
            : `$${fmtCOP(coupon.discountValue)}`
          }
        </p>
        <p className="text-[9px] font-bold uppercase tracking-widest text-white/75 mt-1.5 text-center">
          {coupon.discountType === "percentage" ? "DE DESCUENTO" : "DE AHORRO"}
        </p>

        {/* Notch circle — right center */}
        <div className="absolute -right-[10px] top-1/2 -translate-y-1/2 w-[20px] h-[20px] rounded-full bg-gray-50 border border-gray-200 z-10" />
      </div>

      {/* ── Perforated divider ──────────────────────────── */}
      <div className="relative self-stretch flex-shrink-0 w-px">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "repeating-linear-gradient(to bottom, #e5e7eb 0px, #e5e7eb 6px, transparent 6px, transparent 12px)",
            width: "1px",
          }}
        />
      </div>

      {/* ── Right content ───────────────────────────────── */}
      <div className="flex-1 bg-white rounded-r-2xl p-4 min-w-0">
        {/* Row 1: code + badge */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base sm:text-lg font-black font-mono text-gray-900 tracking-wider truncate">
              {coupon.code}
            </span>
            <button
              onClick={handleCopy}
              className={`shrink-0 flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg border transition ${
                copied
                  ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                  : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
              }`}
            >
              {copied ? <><Check size={9} /> Copiado</> : <><Copy size={9} /> Copiar</>}
            </button>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {!isMine && (
              <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">
                <Globe size={9} /> Plataforma
              </span>
            )}
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge.cls}`}>
              {badge.label}
            </span>
          </div>
        </div>

        {/* Description */}
        {coupon.description && (
          <p className="text-xs text-gray-500 mb-2.5 line-clamp-1">{coupon.description}</p>
        )}

        {/* Metadata chips */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-gray-500 mb-3">
          {coupon.minOrderAmount > 0 && (
            <span className="flex items-center gap-1">
              <Tag size={9} />
              Mín: <b className="text-gray-700 ml-0.5">${fmtCOP(coupon.minOrderAmount)}</b>
            </span>
          )}
          {coupon.endDate && (
            <span className={`flex items-center gap-1 ${days !== null && days <= 7 && !isExpired ? "text-amber-600 font-semibold" : ""}`}>
              <Calendar size={9} />
              Hasta: <b className="ml-0.5">{new Date(coupon.endDate).toLocaleDateString("es-CO")}</b>
            </span>
          )}
          <span className="flex items-center gap-1">
            <Users size={9} />
            <b className="text-gray-700">{coupon.usedCount}</b>
            <span> / </span>
            {isUnlimited
              ? <Infinity size={9} className="text-gray-700" />
              : <b className="text-gray-700">{coupon.maxUses}</b>
            }
            <span className="ml-0.5">usos</span>
          </span>
        </div>

        {/* Usage progress bar */}
        {!isUnlimited && coupon.maxUses > 0 && (
          <div className="mb-3">
            <div className="flex justify-between text-[9px] text-gray-400 mb-1">
              <span>{usagePct}% usado</span>
              <span>{coupon.maxUses - coupon.usedCount} restantes</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  usagePct >= 90 ? "bg-red-400"
                  : usagePct >= 70 ? "bg-amber-400"
                  : strip
                }`}
                style={{ width: `${usagePct}%` }}
              />
            </div>
          </div>
        )}

        {/* Expired warning */}
        {isExpired && (
          <div className="flex items-center gap-1.5 text-[10px] text-red-600 font-semibold mb-2">
            <AlertCircle size={10} /> Este cupón está vencido
          </div>
        )}

        {/* Acciones — solo se pueden editar/eliminar/activar los cupones propios;
            uno de plataforma se gestiona desde el panel admin. */}
        {isMine ? (
          <div className="flex justify-end gap-2">
            <button
              onClick={() => onEdit(coupon)}
              className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
            >
              <Pencil size={12} /> Editar
            </button>
            <button
              onClick={() => onDelete(coupon)}
              className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition"
            >
              <Trash2 size={12} />
            </button>
            <button
              onClick={() => onToggle(getId(coupon))}
              className={`flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg border transition ${
                coupon.active
                  ? "border-red-200 text-red-600 hover:bg-red-50"
                  : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              }`}
            >
              {coupon.active
                ? <><ToggleRight size={13} /> Desactivar</>
                : <><ToggleLeft  size={13} /> Activar</>
              }
            </button>
          </div>
        ) : (
          <p className="text-[10px] text-gray-400 text-right">Gestionado por Qkiere</p>
        )}
      </div>
    </motion.div>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────
function EmptyState({ onCreate }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center py-20 text-center"
    >
      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
        <Tag size={28} className="text-gray-300" />
      </div>
      <p className="font-bold text-gray-600 mb-1">Sin cupones creados</p>
      <p className="text-sm text-gray-400 mb-5 max-w-xs">
        Crea tu primer cupón de descuento para atraer más clientes
      </p>
      <button
        onClick={onCreate}
        className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition"
      >
        <Plus size={15} /> Crear primer cupón
      </button>
    </motion.div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function CompanyCoupons() {
  const navigate  = useNavigate();
  const user      = JSON.parse(localStorage.getItem("user"));
  const companyId = user?.company?.id ?? user?.company?._id;

  const [coupons,    setCoupons]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [openModal,  setOpenModal]  = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [filterTab,  setFilterTab]  = useState("all");

  const loadCoupons = async () => {
    try {
      const res = await API.get(`/coupons/company/${companyId}`);
      const raw = res.data?.data?.coupons ?? res.data?.coupons ?? res.data?.data ?? res.data;
      setCoupons(Array.isArray(raw) ? raw : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error cargando cupones");
    } finally {
      setLoading(false);
    }
  };

  const toggleCoupon = async (id) => {
    try {
      await API.patch(`/coupons/${id}/toggle`);
      loadCoupons();
    } catch {
      toast.error("Error cambiando estado del cupón");
    }
  };

  const deleteCoupon = async (coupon) => {
    if (!window.confirm(`¿Eliminar el cupón "${coupon.code}"? Esta acción no se puede deshacer.`)) return;
    try {
      await API.delete(`/coupons/${getId(coupon)}`);
      toast.success("Cupón eliminado");
      loadCoupons();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error eliminando el cupón");
    }
  };

  useEffect(() => { loadCoupons(); }, []); // eslint-disable-line

  // Filter logic
  const filtered = coupons.filter(c => {
    const expired = c.endDate && new Date(c.endDate) < new Date();
    if (filterTab === "active")   return c.active && !expired;
    if (filterTab === "inactive") return !c.active || expired;
    return true;
  });

  const activeCount   = coupons.filter(c => c.active && !(c.endDate && new Date(c.endDate) < new Date())).length;
  const inactiveCount = coupons.length - activeCount;

  const TABS = [
    { key: "all",      label: "Todos",     count: coupons.length   },
    { key: "active",   label: "Activos",   count: activeCount      },
    { key: "inactive", label: "Inactivos", count: inactiveCount    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-16">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition"
          >
            <ArrowLeft size={16} /> Volver
          </button>
          <div className="w-px h-4 bg-gray-200" />
          <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
            <Tag size={20} className="text-indigo-600" />
            Cupones
          </h1>
        </div>
        <button
          onClick={() => setOpenModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition shadow-sm"
        >
          <Plus size={15} /> Crear cupón
        </button>
      </div>

      {/* Filter tabs */}
      {coupons.length > 0 && (
        <div className="flex gap-1.5 mb-5">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilterTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                filterTab === tab.key
                  ? "bg-gray-900 text-white"
                  : "bg-white border border-gray-200 text-gray-500 hover:bg-gray-50"
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                  filterTab === tab.key ? "bg-white/20" : "bg-gray-100 text-gray-500"
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {Array(3).fill(0).map((_, i) => (
            <div key={i} className="h-36 bg-white rounded-2xl animate-pulse border border-gray-100" />
          ))}
        </div>
      ) : coupons.length === 0 ? (
        <EmptyState onCreate={() => setOpenModal(true)} />
      ) : filtered.length === 0 ? (
        <div className="text-center py-14 text-gray-400">
          <p className="font-semibold text-sm">Sin cupones en esta categoría</p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {filtered.map(c => (
              <CouponCard
                key={getId(c)}
                coupon={c}
                ownCompanyId={companyId}
                onToggle={toggleCoupon}
                onEdit={setEditingCoupon}
                onDelete={deleteCoupon}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {openModal && (
        <CreateCouponModal
          onClose={() => setOpenModal(false)}
          onSaved={loadCoupons}
        />
      )}

      {editingCoupon && (
        <CreateCouponModal
          coupon={editingCoupon}
          onClose={() => setEditingCoupon(null)}
          onSaved={loadCoupons}
        />
      )}
    </div>
  );
}
